<template>
  <div class="mini-chart" ref="chartContainer"></div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: 'line', // line, bar, area
    validator: (value) => ['line', 'bar', 'area'].includes(value)
  },
  data: {
    type: Array,
    default: () => []
  },
  width: {
    type: Number,
    default: 60
  },
  height: {
    type: Number,
    default: 20
  },
  color: {
    type: String,
    default: '#409eff'
  }
});

const chartContainer = ref(null);

const drawChart = () => {
  if (!chartContainer.value || !props.data.length) return;
  
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = props.width;
  canvas.height = props.height;
  canvas.style.width = props.width + 'px';
  canvas.style.height = props.height + 'px';
  
  // 清空容器并添加canvas
  chartContainer.value.innerHTML = '';
  chartContainer.value.appendChild(canvas);
  
  // 计算数据范围
  const maxValue = Math.max(...props.data);
  const minValue = Math.min(...props.data);
  const range = maxValue - minValue || 1;
  
  // 绘制图表
  ctx.strokeStyle = props.color;
  ctx.fillStyle = props.color;
  ctx.lineWidth = 1;
  
  if (props.type === 'line') {
    drawLineChart(ctx, range, minValue);
  } else if (props.type === 'bar') {
    drawBarChart(ctx, range, minValue);
  } else if (props.type === 'area') {
    drawAreaChart(ctx, range, minValue);
  }
};

const drawLineChart = (ctx, range, minValue) => {
  ctx.beginPath();
  
  props.data.forEach((value, index) => {
    const x = (index / (props.data.length - 1)) * props.width;
    const y = props.height - ((value - minValue) / range) * props.height;
    
    if (index === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  });
  
  ctx.stroke();
};

const drawBarChart = (ctx, range, minValue) => {
  const barWidth = props.width / props.data.length;
  
  props.data.forEach((value, index) => {
    const barHeight = ((value - minValue) / range) * props.height;
    const x = index * barWidth;
    const y = props.height - barHeight;
    
    ctx.fillRect(x, y, barWidth - 1, barHeight);
  });
};

const drawAreaChart = (ctx, range, minValue) => {
  ctx.beginPath();
  ctx.moveTo(0, props.height);
  
  props.data.forEach((value, index) => {
    const x = (index / (props.data.length - 1)) * props.width;
    const y = props.height - ((value - minValue) / range) * props.height;
    ctx.lineTo(x, y);
  });
  
  ctx.lineTo(props.width, props.height);
  ctx.closePath();
  
  ctx.globalAlpha = 0.3;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.stroke();
};

// 监听数据变化重新绘制
watch(() => props.data, drawChart, { deep: true });
watch(() => [props.type, props.color, props.width, props.height], drawChart);

onMounted(() => {
  drawChart();
});
</script>

<style scoped>
.mini-chart {
  display: inline-block;
  vertical-align: middle;
}
</style> 